import React, { useState, useEffect, useMemo } from 'react';
import { Check, Star, ArrowRight, Plus, Loader2, Search, SlidersHorizontal, X, Eye, RotateCcw } from 'lucide-react';
import { subscribeToCategories, subscribeToPackages, subscribeToTerms, subscribeToAddOns } from '../services/firestore';
import { subscribeToGallery } from '../services/firebase';
import TiltCard from './ui/TiltCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [terms, setTerms] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPackagePreview, setSelectedPackagePreview] = useState(null);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [expandedAddOns, setExpandedAddOns] = useState({});

  // Advanced filters
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let categoriesUnsubscribe;
    let packagesUnsubscribe;
    let termsUnsubscribe;
    let addOnsUnsubscribe;
    let galleryUnsubscribe;

    const loadData = () => {
      try {
        categoriesUnsubscribe = subscribeToCategories((data) => {
          setCategories(data);
        });

        packagesUnsubscribe = subscribeToPackages((data) => {
          setPackages(data);
          setLoading(false);
        });

        addOnsUnsubscribe = subscribeToAddOns((data) => {
          setAddOns(data);
        });

        galleryUnsubscribe = subscribeToGallery((data) => {
          setGalleryItems(data || []);
        });

        termsUnsubscribe = subscribeToTerms((data) => {
          setTerms(data || []);
        });
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Gagal memuat data. Silakan coba lagi nanti.');
        setLoading(false);
      }

      return () => {
        if (categoriesUnsubscribe) categoriesUnsubscribe();
        if (packagesUnsubscribe) packagesUnsubscribe();
        if (addOnsUnsubscribe) addOnsUnsubscribe();
        if (galleryUnsubscribe) galleryUnsubscribe();
        if (termsUnsubscribe) termsUnsubscribe();
      };
    };

    loadData();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Memoized category name lookup for O(1) performance
  const getCategoryName = useMemo(() => {
    const categoryMap = new Map(categories.map(c => [c.id, c.name]));
    return (categoryId) => categoryMap.get(categoryId) || '';
  }, [categories]);

  // Memoized add-on lookup by ID
  const addOnMap = useMemo(() => {
    return new Map(addOns.map(a => [a.id, a]));
  }, [addOns]);

  const galleryImageMap = useMemo(() => {
    return new Map(galleryItems.map((item) => [item.id, item]));
  }, [galleryItems]);

  const getPackageImage = (pkg) => {
    if (!pkg) return null;
    const galleryIds = Array.isArray(pkg.galleryImageIds) ? pkg.galleryImageIds : [];
    const galleryImageUrl = galleryIds.length > 0 ? galleryImageMap.get(galleryIds[0])?.image : null;
    return galleryImageUrl || pkg.images?.[0] || pkg.image || null;
  };

  const toggleExpandedAddOns = (packageId) => {
    setExpandedAddOns(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
  };

  // Memoized transformed packages from Firestore
  const allPackages = useMemo(() => {
    return packages
      .filter(pkg => pkg.isActive !== false)
      .map(pkg => ({
        ...pkg,
        category: getCategoryName(pkg.categoryId),
        subcategory: pkg.subcategoryName || null,
        features: pkg.includes || [],
      }));
  }, [packages, getCategoryName]);

  // Get unique categories for filter
  const categoryOptions = useMemo(() => {
    const uniqueCategories = new Set(
      packages
        .filter(pkg => pkg.isActive !== false)
        .map(pkg => getCategoryName(pkg.categoryId))
        .filter(Boolean)
    );
    return ['all', ...Array.from(uniqueCategories)];
  }, [packages, getCategoryName]);

  // Calculate price range from available packages
  const availablePriceRange = useMemo(() => {
    const prices = allPackages.map(pkg => pkg.price || 0);
    return {
      min: Math.min(...prices, 0),
      max: Math.max(...prices, 0)
    };
  }, [allPackages]);

  // Initialize price range on first load
  useEffect(() => {
    if (availablePriceRange.max > 0 && priceRange.max === 0) {
      setPriceRange(availablePriceRange);
    }
  }, [availablePriceRange]);

  useEffect(() => {
    if (!selectedPackagePreview) return;

    setPreviewImageIndex(0);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedPackagePreview(null);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedPackagePreview]);

  const previewImages = useMemo(() => {
    if (!selectedPackagePreview) return [];
    const galleryMap = new Map(galleryItems.map((item) => [item.id, item]));
    const galleryIds = Array.isArray(selectedPackagePreview.galleryImageIds)
      ? selectedPackagePreview.galleryImageIds
      : [];
    const galleryImages = galleryIds
      .map((id) => galleryMap.get(id)?.image)
      .filter(Boolean);
    if (galleryImages.length > 0) return galleryImages;
    const images = Array.isArray(selectedPackagePreview.images)
      ? selectedPackagePreview.images.filter(Boolean)
      : [];
    if (images.length > 0) return images;
    return selectedPackagePreview.image ? [selectedPackagePreview.image] : [];
  }, [selectedPackagePreview, galleryItems]);

  const handlePrevImage = () => {
    setPreviewImageIndex((prev) =>
      prev === 0 ? previewImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setPreviewImageIndex((prev) =>
      prev === previewImages.length - 1 ? 0 : prev + 1
    );
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedCategories.length > 0) count++;
    if (priceRange.min > availablePriceRange.min || priceRange.max < availablePriceRange.max) count++;
    return count;
  }, [searchQuery, selectedCategories, priceRange, availablePriceRange]);

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setPriceRange(availablePriceRange);
    setActiveCategory('all');
  };

  // Advanced filtering logic
  const filteredPackages = useMemo(() => {
    return allPackages.filter(pkg => {
      // Category filter (backward compatibility)
      const categoryMatch = activeCategory === 'all' || pkg.category === activeCategory;

      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const searchMatch = !searchQuery ||
        pkg.name?.toLowerCase().includes(searchLower) ||
        pkg.type?.toLowerCase().includes(searchLower) ||
        pkg.category?.toLowerCase().includes(searchLower) ||
        pkg.features?.some(f => f.toLowerCase().includes(searchLower));

      // Multi-category filter
      const multiCategoryMatch = selectedCategories.length === 0 ||
        selectedCategories.includes(pkg.category);

      // Price range filter
      const priceMatch = (!priceRange.min || pkg.price >= priceRange.min) &&
        (!priceRange.max || pkg.price <= priceRange.max);

      return categoryMatch && searchMatch && multiCategoryMatch && priceMatch;
    });
  }, [allPackages, activeCategory, searchQuery, selectedCategories, priceRange]);

  if (loading) {
    return (
      <section id="services" className="py-20 sm:py-32 bg-gradient-to-b from-black to-[#0A0A0A] relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF7A]" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="services" className="py-20 sm:py-32 bg-gradient-to-b from-black to-[#0A0A0A] relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (allPackages.length === 0) {
    return (
      <section id="services" className="py-20 sm:py-32 bg-gradient-to-b from-black to-[#0A0A0A] relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <p className="text-[#F7F5F2]/70">Belum ada paket layanan tersedia saat ini.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-16 xs:py-20 sm:py-24 md:py-28 lg:py-32 bg-gradient-to-b from-black to-[#0A0A0A] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-0 w-64 h-64 xs:w-80 xs:h-80 md:w-96 md:h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-0 w-64 h-64 xs:w-80 xs:h-80 md:w-96 md:h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 xs:px-6 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 xs:mb-12 md:mb-16">
          <span className="inline-block px-3 xs:px-4 py-1.5 xs:py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs xs:text-sm font-semibold mb-3 xs:mb-4">
            Layanan Kami
          </span>
          <h2 className="font-display text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#F7F5F2] mb-4 xs:mb-6">
            Paket Henna Pilihan
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4 xs:mb-6 md:mb-8">
            <span className="block w-8 xs:w-10 md:w-14 h-px bg-gradient-to-l from-[#D4AF7A]/50 to-transparent"></span>
            <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
            <span className="block w-16 xs:w-20 md:w-24 h-[2px] bg-gradient-to-r from-[#C9A882] via-[#D4AF7A] to-[#E5C99A]"></span>
            <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
            <span className="block w-8 xs:w-10 md:w-14 h-px bg-gradient-to-r from-[#D4AF7A]/50 to-transparent"></span>
          </div>
          <p className="text-sm xs:text-base md:text-lg text-[#F7F5F2]/70 max-w-2xl mx-auto px-2">
            Pilih paket yang sesuai dengan kebutuhan acara spesial Anda
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-4 xs:mb-6">
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-3 xs:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 xs:w-5 xs:h-5 text-[#F7F5F2]/50" />
            <input
              type="text"
              placeholder="Cari paket..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 xs:pl-12 pr-20 xs:pr-24 py-2.5 xs:py-3 min-h-[44px] bg-white/5 border border-[#D4AF7A]/30 rounded-lg text-[#F7F5F2] placeholder-[#F7F5F2]/50 focus:outline-none focus:border-[#D4AF7A] focus:ring-2 focus:ring-[#D4AF7A]/50 text-sm xs:text-base"
            />
            <button
              onClick={() => setShowFilters(true)}
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 px-2 xs:px-3 py-2 min-h-[36px] min-w-[36px] xs:min-h-[40px] rounded-lg text-xs xs:text-sm font-medium flex items-center gap-1.5 xs:gap-2 transition-all ${
                activeFilterCount > 0
                  ? 'bg-[#D4AF7A] text-black'
                  : 'bg-white/10 text-[#F7F5F2] hover:bg-white/20'
              } focus:outline-none focus:ring-2 focus:ring-[#D4AF7A]`}
              aria-label="Buka filter"
            >
              <SlidersHorizontal size={14} className="xs:w-4 xs:h-4" />
              <span className="hidden xs:inline">Filter</span>
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-black/20 rounded-full text-[10px] xs:text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Dialog/Modal */}
        <Dialog open={showFilters} onOpenChange={setShowFilters}>
          <DialogContent className="max-w-lg bg-[#0A0A0A] border border-[#D4AF7A]/20 text-[#F7F5F2]">
            <DialogHeader className="border-b border-[#D4AF7A]/10 pb-4">
              <DialogTitle className="text-lg font-bold text-[#F7F5F2] font-display">
                Filter Paket
              </DialogTitle>
              {activeFilterCount > 0 && (
                <button
                  onClick={() => { resetFilters(); setShowFilters(false); }}
                  className="absolute right-12 top-4 flex items-center gap-1.5 text-xs text-[#D4AF7A]/70 hover:text-[#D4AF7A] transition-colors"
                >
                  <RotateCcw size={12} />
                  Reset
                </button>
              )}
            </DialogHeader>

            <div className="space-y-6 py-2">
              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2]/80 mb-3 flex items-center gap-2">
                  <span className="w-1 h-3 bg-[#D4AF7A]/50 rounded-full" />
                  Rentang Harga
                </label>
                <div className="space-y-4 px-1">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#F7F5F2]/50">Minimum</span>
                      <span className="text-xs font-medium text-[#D4AF7A]">{formatPrice(priceRange.min)}</span>
                    </div>
                    <input
                      type="range"
                      min={availablePriceRange.min}
                      max={availablePriceRange.max}
                      step={100000}
                      value={priceRange.min}
                      onChange={(e) => setPriceRange({ ...priceRange, min: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#D4AF7A] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#D4AF7A] [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(212,175,122,0.4)]"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#F7F5F2]/50">Maksimum</span>
                      <span className="text-xs font-medium text-[#D4AF7A]">{formatPrice(priceRange.max)}</span>
                    </div>
                    <input
                      type="range"
                      min={availablePriceRange.min}
                      max={availablePriceRange.max}
                      step={100000}
                      value={priceRange.max}
                      onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#D4AF7A] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#D4AF7A] [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(212,175,122,0.4)]"
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-[#D4AF7A]/10" />

              {/* Multi-category Selection */}
              <div>
                <label className="block text-sm font-medium text-[#F7F5F2]/80 mb-3 flex items-center gap-2">
                  <span className="w-1 h-3 bg-[#D4AF7A]/50 rounded-full" />
                  Kategori
                </label>
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.filter(c => c !== 'all').map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        if (selectedCategories.includes(category)) {
                          setSelectedCategories(prev => prev.filter(c => c !== category));
                        } else {
                          setSelectedCategories(prev => [...prev, category]);
                        }
                      }}
                      className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all ${
                        selectedCategories.includes(category)
                          ? 'bg-[#D4AF7A] text-black shadow-[0_0_12px_rgba(212,175,122,0.25)]'
                          : 'bg-white/5 text-[#F7F5F2]/70 border border-[#D4AF7A]/20 hover:bg-white/10 hover:border-[#D4AF7A]/40'
                      }`}
                    >
                      {selectedCategories.includes(category) && <Check size={11} className="inline mr-1.5" />}
                      {category}
                    </button>
                  ))}
                  {categoryOptions.filter(c => c !== 'all').length === 0 && (
                    <p className="text-xs text-[#F7F5F2]/40">Tidak ada kategori tersedia</p>
                  )}
                </div>
              </div>
            </div>

            {/* Apply / Close */}
            <div className="border-t border-[#D4AF7A]/10 pt-4 flex items-center justify-between">
              <p className="text-xs text-[#F7F5F2]/40">
                {filteredPackages.length} paket ditemukan
              </p>
              <button
                onClick={() => setShowFilters(false)}
                className="px-5 py-2 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                Terapkan
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Category Filter (Quick) */}
        <div className="flex flex-wrap justify-center gap-2 xs:gap-3 mb-4">
          {categoryOptions.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-3 xs:px-4 sm:px-5 md:px-6 py-2 xs:py-2.5 min-h-[40px] xs:min-h-[44px] rounded-full text-xs xs:text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] ${
                activeCategory === category
                  ? 'bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black shadow-gold-lg shadow-[#D4AF7A]/40 scale-105'
                  : 'bg-white/5 text-[#F7F5F2] border border-[#D4AF7A]/30 hover:bg-white/10 hover:border-[#D4AF7A]/50'
              }`}
              aria-label={`Filter kategori ${category === 'all' ? 'Semua Paket' : category}`}
              aria-pressed={activeCategory === category}
            >
              {category === 'all' ? 'Semua' : category}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <div className="text-center mb-4">
          <p className="text-[#F7F5F2]/60 text-xs xs:text-sm">
            {filteredPackages.length} paket ditemukan
            {activeFilterCount > 0 && (
              <span className="ml-2">
                (<span className="text-[#D4AF7A]">{activeFilterCount} filter aktif</span>)
                <button
                  onClick={resetFilters}
                  className="ml-2 text-[#D4AF7A] hover:underline focus:outline-none focus:ring-2 focus:ring-[#D4AF7A] rounded"
                >
                  Reset
                </button>
              </span>
            )}
          </p>
        </div>

        {/* Services Grid */}
        {filteredPackages.length === 0 ? (
          <div className="text-center py-12 xs:py-16 bg-white/[0.04] border border-[#D4AF7A]/15 rounded-xl">
            <Search className="w-12 h-12 xs:w-16 xs:h-16 text-[#D4AF7A]/50 mx-auto mb-4" />
            <h3 className="text-lg xs:text-xl font-semibold text-[#F7F5F2] mb-2">
              Tidak ada paket yang cocok
            </h3>
            <p className="text-[#F7F5F2]/70 mb-4 text-sm xs:text-base">
              Coba atur ulang filter atau kata kunci pencarian Anda
            </p>
            <button
              onClick={resetFilters}
              className="px-4 xs:px-6 py-2 min-h-[44px] btn-gold-premium rounded-lg text-sm xs:text-base"
            >
              Reset Filter
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6 md:gap-8">
            {filteredPackages.map((pkg, index) => (
            <TiltCard key={index} tiltDegree={6} scale={1.015} className="group">
            <div
              className="relative bg-white/[0.04] border border-[#D4AF7A]/15 rounded-xl xs:rounded-2xl p-4 xs:p-6 hover:bg-white/[0.07] hover:border-[#D4AF7A]/40 transition-all duration-300"
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-3 left-4 z-10">
                  <div className="px-4 py-1.5 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black text-xs font-bold rounded-full flex items-center gap-1 shadow-lg shadow-[#D4AF7A]/30 animate-gold-pulse">
                    <Star size={14} fill="currentColor" />
                    POPULAR
                  </div>
                </div>
              )}

              {/* Category Tag */}
              <div className="inline-block px-3 py-1 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs font-semibold mb-2">
                {pkg.category}
              </div>

              {/* Subcategory Tag */}
              {pkg.subcategory && (
                <div className="inline-block ml-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full text-[#F7F5F2]/70 text-xs font-medium mb-2">
                  {pkg.subcategory}
                </div>
              )}

              {/* Package Name */}
              <h3 className="text-xl sm:text-2xl font-bold text-[#F7F5F2] mb-2 mt-2">{pkg.name}</h3>
              {pkg.type && <p className="text-[#F7F5F2]/60 text-sm mb-4">{pkg.type}</p>}

              {/* Price */}
              <div className="mb-6">
                <div className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-[#D4AF7A]">
                  {formatPrice(pkg.price)}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2.5 mb-4">
                {pkg.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-5 h-5 bg-[#D4AF7A]/20 rounded-full flex items-center justify-center mt-0.5 group-hover:bg-[#D4AF7A]/30 transition-colors duration-300">
                      <Check size={14} className="text-[#D4AF7A]" />
                    </div>
                    <span className="text-[#F7F5F2]/80 text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Tambahan */}
              {(() => {
                const packageAddOns = (pkg.availableAddOns || [])
                  .map(id => addOnMap.get(id))
                  .filter(Boolean);
                if (packageAddOns.length === 0) return null;

                const isExpanded = expandedAddOns[pkg.id];
                const visibleAddOns = isExpanded ? packageAddOns : packageAddOns.slice(0, 2);
                const hiddenCount = packageAddOns.length - 2;

                return (
                  <div className="mb-6">
                    <p className="text-xs font-medium text-[#D4AF7A] mb-1.5">
                      Tambahan
                    </p>
                    <div className="p-3 bg-[#D4AF7A]/5 border border-[#D4AF7A]/20 rounded-lg space-y-1.5">
                      {visibleAddOns.map(addon => (
                        <div key={addon.id} className="flex items-center justify-between">
                          <span className="text-xs text-[#F7F5F2]/80">{addon.name}</span>
                          <span className="text-xs font-medium text-[#D4AF7A]">
                            {formatPrice(addon.price)}
                            {addon.unitType !== 'flat' && <span className="text-[10px] text-[#F7F5F2]/50">/{addon.unitType}</span>}
                          </span>
                        </div>
                      ))}
                      {hiddenCount > 0 && (
                        <button
                          onClick={() => toggleExpandedAddOns(pkg.id)}
                          className="text-xs text-[#D4AF7A]/70 hover:text-[#D4AF7A] transition-colors pt-1"
                        >
                          + {hiddenCount} lainnya
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Detail Button */}
              <button
                type="button"
                onClick={() => setSelectedPackagePreview(pkg)}
                className="w-full mb-3 py-2.5 btn-gold-outline font-semibold rounded-lg flex items-center justify-center gap-2"
              >
                <Eye size={16} />
                Lihat Detail
              </button>

              {/* CTA Button */}
              <a
                href={`https://wa.me/6289526775445?text=Halo teh, aku mau booking ${pkg.name} untuk tanggal ....`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 btn-gold-premium rounded-lg text-center flex items-center justify-center gap-2 btn-icon-slide"
              >
                Pesan Sekarang
                <ArrowRight size={18} className="icon-slide" />
              </a>
            </div>
          </TiltCard>
          ))}
          </div>
        )}

        {/* Terms and Conditions Section */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#F7F5F2] mb-4">
              Syarat &amp; Ketentuan
            </h3>
            <div className="flex items-center justify-center gap-2">
              <span className="block w-8 h-px bg-gradient-to-l from-[#D4AF7A]/50 to-transparent"></span>
              <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
              <span className="block w-16 h-[2px] bg-gradient-to-r from-[#C9A882] via-[#D4AF7A] to-[#E5C99A]"></span>
              <span className="block w-2 h-2 rotate-45 bg-[#D4AF7A]"></span>
              <span className="block w-8 h-px bg-gradient-to-r from-[#D4AF7A]/50 to-transparent"></span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {terms.map((term, idx) => (
              <TiltCard key={term.id} tiltDegree={4} scale={1.01}>
              <div
                className="relative bg-white/[0.04] border border-[#D4AF7A]/15 rounded-xl p-6 hover:bg-white/[0.07] hover:border-[#D4AF7A]/40 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-[#C9A882] to-[#D4AF7A] rounded-lg flex items-center justify-center mb-4 text-black font-bold text-sm shadow-md">
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <h4 className="text-base sm:text-lg font-semibold text-[#F7F5F2] mb-3 group-hover:text-[#D4AF7A] transition-colors duration-300">{term.title}</h4>
                <p className="text-[#F7F5F2]/70 text-sm leading-relaxed">{term.description}</p>
                {term.details && (
                  <ul className="mt-3 space-y-2">
                    {term.details.map((detail, dIdx) => (
                      <li key={dIdx} className="flex items-start gap-2">
                        <Check size={14} className="text-[#D4AF7A] mt-0.5 flex-shrink-0" />
                        <span className="text-[#F7F5F2]/60 text-xs">{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TiltCard>
            ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-block px-6 py-4 bg-white/[0.04] border border-[#D4AF7A]/15 rounded-xl hover:bg-white/[0.07] hover:border-[#D4AF7A]/40 transition-all duration-300">
            <p className="text-[#F7F5F2]/80">
              Butuh paket custom atau konsultasi?{' '}
              <a
                href="https://wa.me/6289526775445"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-[#D4AF7A] font-semibold hover:underline hover:text-[#E5C99A] transition-colors"
              >
                Hubungi Kami
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      {selectedPackagePreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-0 md:p-4"
          onClick={() => setSelectedPackagePreview(null)}
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          <button
            onClick={() => setSelectedPackagePreview(null)}
            className="hidden md:flex absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full text-[#F7F5F2] hover:text-[#D4AF7A] transition-all duration-200 hover:scale-110"
            aria-label="Tutup detail"
          >
            <X size={20} />
          </button>

          <div
            className="relative max-w-3xl w-full h-screen md:h-auto md:max-h-[90vh] overflow-y-auto rounded-none md:rounded-2xl bg-gradient-to-b from-[#0A0A0A] to-black md:border md:border-[#D4AF7A]/20"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          >
            {/* Mobile close header */}
            <div className="sticky top-0 z-20 flex items-center justify-between px-4 md:px-6 py-3 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#D4AF7A]/10 md:hidden">
              <span className="text-sm font-semibold text-[#F7F5F2]">Detail Paket</span>
              <button
                onClick={() => setSelectedPackagePreview(null)}
                className="p-2 -mr-2 text-[#F7F5F2]/70 hover:text-[#D4AF7A] transition-colors active:scale-95"
                aria-label="Tutup"
              >
                <X size={20} />
              </button>
            </div>

            {/* Image with gold accent */}
            {previewImages.length > 0 && (
              <div className="relative w-full bg-black/40">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#C9A882] via-[#D4AF7A] to-[#E5C99A] z-20"></div>

                <div className="relative z-0 flex items-center justify-center h-[46vh] max-h-[420px] sm:h-[52vh] sm:max-h-[520px] w-full">
                  <img
                    src={previewImages[previewImageIndex]}
                    alt={`${selectedPackagePreview.name} ${previewImages.length > 1 ? `(${previewImageIndex + 1}/${previewImages.length})` : ''}`}
                    className="w-full h-full object-contain"
                  />
                </div>

                {previewImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={handlePrevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-black/50 border border-white/10 rounded-full text-[#F7F5F2] hover:text-[#D4AF7A] hover:bg-black/70 transition-all"
                      aria-label="Gambar sebelumnya"
                    >
                      <ArrowRight size={18} className="rotate-180" />
                    </button>
                    <button
                      type="button"
                      onClick={handleNextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2.5 bg-black/50 border border-white/10 rounded-full text-[#F7F5F2] hover:text-[#D4AF7A] hover:bg-black/70 transition-all"
                      aria-label="Gambar berikutnya"
                    >
                      <ArrowRight size={18} />
                    </button>
                  </>
                )}

                {previewImages.length > 1 && (
                  <div className="flex items-center gap-2 overflow-x-auto px-4 py-3 bg-black/40 border-t border-white/5">
                    {previewImages.map((imageUrl, idx) => {
                      const isActive = idx === previewImageIndex;
                      return (
                        <button
                          key={`${imageUrl}-${idx}`}
                          type="button"
                          onClick={() => setPreviewImageIndex(idx)}
                          className={`relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-md border transition-all ${
                            isActive
                              ? 'border-[#D4AF7A] ring-2 ring-[#D4AF7A]/40'
                              : 'border-white/10 hover:border-[#D4AF7A]/60'
                          }`}
                          aria-label={`Pilih gambar ${idx + 1}`}
                          aria-pressed={isActive}
                        >
                          <img
                            src={imageUrl}
                            alt={`${selectedPackagePreview.name} thumbnail ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-6 md:p-8 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-xs font-semibold">
                    {selectedPackagePreview.category}
                  </span>
                  {selectedPackagePreview.subcategory && (
                    <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-[#F7F5F2]/70 text-xs font-medium">
                      {selectedPackagePreview.subcategory}
                    </span>
                  )}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#F7F5F2]">
                  {selectedPackagePreview.name}
                </h3>
              </div>

              {/* Description */}
              {selectedPackagePreview.description && (
                <div>
                  <h4 className="text-sm font-semibold text-[#D4AF7A] uppercase tracking-wider mb-2">
                    Deskripsi
                  </h4>
                  <p className="text-[#F7F5F2]/80 text-sm leading-relaxed">
                    {selectedPackagePreview.description}
                  </p>
                </div>
              )}

              {/* Price */}
              <div>
                <h4 className="text-sm font-semibold text-[#D4AF7A] uppercase tracking-wider mb-2">
                  Harga
                </h4>
                <p className="text-3xl font-bold text-[#D4AF7A]">
                  {formatPrice(selectedPackagePreview.price)}
                </p>
              </div>

              {/* Features */}
              {selectedPackagePreview.features && selectedPackagePreview.features.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-[#D4AF7A] uppercase tracking-wider mb-2">
                    Termasuk
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedPackagePreview.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check size={14} className="text-[#D4AF7A] mt-0.5 flex-shrink-0" />
                        <span className="text-[#F7F5F2]/80 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA */}
              <a
                href={`https://wa.me/6289526775445?text=Halo teh, aku mau booking ${selectedPackagePreview.name} untuk tanggal ....`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 btn-gold-premium rounded-lg text-center"
              >
                Pesan Sekarang
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;
